// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';

import {
  getSelectedProduct,
  makeSelectProducts,
  getDetailIndex,
  getSelectedFeaturedProduct,
  getFeaturedProductDetailIndex,
  productTypeEnum,
} from 'selectors/productsSelectors';
import {
  selectSubtotal,
  selectHasSponsoredProductInCart,
} from 'selectors/cartSelectors';
import { selectTeamCartProducts } from 'selectors/socialEcommSelectors';

import { VIEW_SIZE } from 'utils/device';
import { btnLocationEnum } from 'utils/segment';
import { CATEGORY_ID } from 'utils/categories';
import cookie, { COOKIE_NAMES } from 'utils/cookie';
import { VERSION } from 'utils/campaign';

import withVersion from 'HOC/withVersion/withVersion';
import withSelectedProductLink from 'HOC/withSelectedProductLink/withSelectedProductLink';
import type { WithSelectedProductLinkInjectedProps } from 'HOC/withSelectedProductLink/withSelectedProductLink';
import { useDecision } from '@optimizely/react-sdk';

import ErrorBoundary from 'shared-components/ErrorBoundary/ErrorBoundary';
import Divider from 'shared-components/Divider/Divider';
import Product from '../Product/Product';
import SponsoredProduct from '../SponsoredProduct/SponsoredProduct';
import GwpProduct from '../GwpProduct/GwpProduct';
import ProductAccordion from '../ProductAccordion/ProductAccordion';
import SponsoredProducts from '../SponsoredProducts/SponsoredProducts';
import GwpProducts from '../GwpProducts/GwpProducts';
import TopTrending from '../TopTrending/TopTrending';
import CategoryHeader from '../../Headers/CategoryHeader/CategoryHeader';
import NoCheckoutRequiredModal from './NoCheckoutRequiredModal/NoCheckoutRequiredModal';

import type { State, BundledProduct, OptimizelyData } from 'sharedTypes';

import styles from './ProductGrid.module.css';

type Ratings = {|
  +averageRating: number,
  +totalReviewCount: number,
|};

type OwnProps = {|
  +type: string,
  +selectedCategoryId: string | null,
  +gwpProducts: Array<BundledProduct>,
  +socialEcommFF: boolean,
  +displayTopTrending?: boolean,
|};

type StateProps = {|
  +viewSize: string,
  +products: Array<BundledProduct>,
  +detailIndex: number | null,
  +selectedProduct: BundledProduct | null,
  +subtotal: number,
  +sponsoredThreshold: number,
  +hasSponsoredProductInCart: boolean,
  +ratings: {| key: number, value: Ratings |},
  +loggedIn: boolean,
|};

type InjectedProps = {|
  ...WithSelectedProductLinkInjectedProps,
  +version: string,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...InjectedProps,
|};

const TOP_TRENDING_DISPLAYED = 20;

const ProductsWrapper = ({ children }) => <div>{children}</div>;

const getWrapper = (type) => {
  switch (type) {
    case productTypeEnum.SPONSORED:
      return SponsoredProducts;
    case productTypeEnum.GWP:
      return GwpProducts;
    default:
      return ProductsWrapper;
  }
};

const resolveProductComponentByType = (type) => {
  switch (type) {
    case productTypeEnum.SPONSORED:
      return SponsoredProduct;
    case productTypeEnum.GWP:
      return GwpProduct;
    default:
      return Product;
  }
};

const resolveClassByType = (type) => {
  switch (type) {
    case productTypeEnum.SPONSORED:
      return styles.sponsoredProducts;
    case productTypeEnum.GWP:
      return styles.gwpProducts;
    default:
      return '';
  }
};

const EXPIRED_ADD_TO_CART_MODAL_DECISION_NAME =
  'auto-checkout-popup-expired-users';

const ProductGrid = (props: Props) => {
  const {
    type,
    viewSize,
    products,
    sponsoredThreshold,
    subtotal,
    detailIndex,
    selectedProduct,
    hasSponsoredProductInCart,
    gwpProducts,
    teamCartProducts,
    socialEcommFF,
    selectedFeaturedProduct,
    featuredProductDetailIndex,
    displayTopTrending,
    selectedCategoryId,
    ratings,
    subscriptionState,
    campaignEndDate,
    loggedIn,
    version,
  } = props;
  const [expiredAddToCartModal, isExpiredAddToCartModalReady] = useDecision(
    EXPIRED_ADD_TO_CART_MODAL_DECISION_NAME,
    {
      autoUpdate: true,
    }
  );
  const [showNoCheckoutRequiredModal, setShowNoCheckoutRequiredModal] =
    React.useState(false);

  const isExpired = subscriptionState === 'EXPIRED';
  const isAddOnsPage = version === VERSION['add-ons'];
  const isEditPage = version === VERSION.edit;
  const isCookiePresent = cookie.get(COOKIE_NAMES.noCheckoutRequired);
  const renderExpiredAddToCartModal =
    isExpired &&
    (isAddOnsPage || isEditPage) &&
    isExpiredAddToCartModalReady &&
    expiredAddToCartModal?.enabled &&
    !isCookiePresent;
  const toggleNoCheckoutRequiredModal = React.useCallback(() => {
    if (renderExpiredAddToCartModal) {
      setShowNoCheckoutRequiredModal((prevState) => !prevState);
    }
  }, [
    showNoCheckoutRequiredModal,
    setShowNoCheckoutRequiredModal,
    renderExpiredAddToCartModal,
  ]);

  if (type === productTypeEnum.GWP) return null;
  if (products.length === 0) return null;
  const Wrapper = getWrapper(type);
  const ProductComponent = resolveProductComponentByType(type);
  const topTrendingProducts = displayTopTrending
    ? products.slice(0, TOP_TRENDING_DISPLAYED)
    : [];
  const displayedProducts = displayTopTrending
    ? products.slice(TOP_TRENDING_DISPLAYED)
    : products;
  const displayFavoritesCategoryHeader =
    selectedCategoryId === CATEGORY_ID.myfavorites &&
    type === productTypeEnum.SOLD_OUT;

  const Products = displayedProducts.map((product) => (
    <ProductComponent
      hasSponsoredProductInCart={hasSponsoredProductInCart}
      sponsoredThreshold={sponsoredThreshold}
      subtotal={subtotal}
      key={product.id}
      rating={ratings[product.id]}
      viewSize={viewSize}
      {...product}
      gwpProducts={gwpProducts}
      teamCart={teamCartProducts[product.id]}
      socialEcommFF={socialEcommFF}
      buttonLocation={btnLocationEnum.productCard}
      positionLocation={
        type === productTypeEnum.FEATURED && btnLocationEnum.pinnedProduct
      }
      showNoCheckoutRequiredModal={toggleNoCheckoutRequiredModal}
      loggedIn={loggedIn}
    />
  ));
  const TopTrendingProducts = topTrendingProducts.map((product) => (
    <Product
      hasSponsoredProductInCart={hasSponsoredProductInCart}
      sponsoredThreshold={sponsoredThreshold}
      subtotal={subtotal}
      rating={ratings[product.id]}
      key={product.id}
      viewSize={viewSize}
      {...product}
      gwpProducts={gwpProducts}
      teamCart={teamCartProducts[product.id]}
      socialEcommFF={socialEcommFF}
      buttonLocation={btnLocationEnum.productCard}
      positionLocation={type === 'FEATURED' && btnLocationEnum.pinnedProduct}
      showNoCheckoutRequiredModal={toggleNoCheckoutRequiredModal}
      loggedIn={loggedIn}
    />
  ));

  const getPosition = (index) => {
    if (displayTopTrending) {
      const isTopTrending = index < TOP_TRENDING_DISPLAYED;
      if (isTopTrending) return index;
      const position = index - TOP_TRENDING_DISPLAYED;
      // subtract 1 on tablets because the last row of Top Trending is odd
      return viewSize === VIEW_SIZE.medium ? position - 1 : position;
    }
    return index;
  };

  const getProductAccordion = (index, product, positionLocation) => {
    if (index !== null && product !== null) {
      const isTopTrending =
        displayTopTrending && index < TOP_TRENDING_DISPLAYED;
      const position = getPosition(index);
      const component = isTopTrending ? TopTrendingProducts : Products;
      component.splice(
        position,
        0,
        <ErrorBoundary key={`product-detail-${product.id}`}>
          <ProductAccordion
            {...product}
            gwpProducts={gwpProducts}
            teamCart={teamCartProducts[product.id]}
            positionLocation={positionLocation}
          />
        </ErrorBoundary>
      );
    }
    return null;
  };

  if (type === productTypeEnum.FEATURED) {
    getProductAccordion(
      featuredProductDetailIndex,
      selectedFeaturedProduct,
      btnLocationEnum.pinnedProduct
    );
  } else {
    getProductAccordion(detailIndex, selectedProduct);
  }

  return (
    <>
      <Wrapper>
        {displayTopTrending && (
          <TopTrending
            products={TopTrendingProducts}
            className={classNames(
              'page-row',
              styles.productGrid,
              styles[viewSize]
            )}
          />
        )}
        {displayFavoritesCategoryHeader && (
          <>
            <Divider className={styles.divider} />
            <CategoryHeader
              title="Out of Stock"
              subtitle="Is the item you want out of stock? Keep checking back to snag
          the item once it’s available again."
            />
          </>
        )}
        <ul
          className={classNames(
            'page-row',
            resolveClassByType(type),
            styles.productGrid,
            styles[viewSize]
          )}
          data-testid="products-grid"
        >
          {Products}
        </ul>
      </Wrapper>
      {renderExpiredAddToCartModal && (
        <NoCheckoutRequiredModal
          campaignEndDate={campaignEndDate}
          isOpen={showNoCheckoutRequiredModal}
          closeModal={toggleNoCheckoutRequiredModal}
        />
      )}
    </>
  );
};

type OwnPropsWithSelectedProductLink = {|
  ...OwnProps,
  ...WithSelectedProductLinkInjectedProps,
|};

type MakeMapStateToProps = (
  state: State,
  props: OwnPropsWithSelectedProductLink
) => (state: State, props: OwnPropsWithSelectedProductLink) => StateProps;

// Using a higher order function for connect in order to share selectProducts and retain memoization.
// See https://redux.js.org/recipes/computing-derived-data#sharing-selectors-across-multiple-components
const makeMapStateToProps: MakeMapStateToProps = (state, props) => {
  const selectProducts = makeSelectProducts();
  return function mapStateToProps(state, props) {
    return {
      detailIndex: getDetailIndex(state, props),
      selectedProduct: getSelectedProduct(state, props),
      subtotal: selectSubtotal(state),
      sponsoredThreshold: state.campaignState.sponsoredThreshold,
      campaignEndDate: state.campaignState.endDate,
      viewSize: state.viewState.viewSize,
      products: selectProducts(state, props),
      hasSponsoredProductInCart: selectHasSponsoredProductInCart(state),
      teamCartProducts: selectTeamCartProducts(state),
      selectedFeaturedProduct: getSelectedFeaturedProduct(state, props),
      featuredProductDetailIndex: getFeaturedProductDetailIndex(state, props),
      ratings: state.productsState.ratings,
      subscriptionState: state.userState.subscriptionState,
      loggedIn: state.userState.loggedIn,
    };
  };
};

ProductGrid.defaultProps = {
  displayTopTrending: false,
};

export default (compose(
  withSelectedProductLink,
  withVersion,
  connect<
    {| ...OwnPropsWithSelectedProductLink, ...StateProps |},
    OwnPropsWithSelectedProductLink,
    StateProps,
    _,
    _,
    _
  >(makeMapStateToProps, {}),
  React.memo
)(ProductGrid): React.AbstractComponent<OwnProps>);
